import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, MapPin, RefreshCw, Sun, Moon } from 'lucide-react';

const PlanetaryHoursExperience = () => {
  const [page, setPage] = useState(0); // 0 = form, 1 = result
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: ''
  });
  const [planetaryHoursData, setPlanetaryHoursData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getPlanetaryHours = (date, time) => {
    const selectedDate = new Date(`${date}T${time}`);
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const hour = selectedDate.getHours();
    
    // Planetary hours sequence (starting from sunrise)
    const planetarySequence = [
      'Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars',
      'Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars',
      'Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars',
      'Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars'
    ];

    // Day rulers (Chaldean order)
    const dayRulers = [
      'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'
    ];

    // Calculate which planetary hour we're in (simplified calculation)
    // In a real implementation, you'd calculate sunrise/sunset times
    const hourOfDay = hour;
    const planetaryHourIndex = Math.floor(hourOfDay / 1.5) % 7; // Simplified
    const currentPlanet = planetarySequence[planetaryHourIndex];
    const dayRuler = dayRulers[dayOfWeek];

    return {
      currentPlanet,
      dayRuler,
      hourOfDay,
      isDayHour: hour >= 6 && hour < 18,
      planetaryHour: Math.floor(hourOfDay / 1.5) + 1
    };
  };

  const getPlanetInfo = (planet) => {
    const planetData = {
      Sun: {
        name: 'Sun',
        icon: '☀️',
        description: 'Power, vitality, success, leadership. Good for career matters, authority, and personal power.',
        color: '#FFD700',
        keywords: ['Power', 'Vitality', 'Success', 'Leadership']
      },
      Moon: {
        name: 'Moon',
        icon: '🌙',
        description: 'Intuition, emotions, dreams, psychic abilities. Good for meditation, divination, and emotional work.',
        color: '#C0C0C0',
        keywords: ['Intuition', 'Emotions', 'Dreams', 'Psychic']
      },
      Mars: {
        name: 'Mars',
        icon: '🔴',
        description: 'Energy, action, courage, conflict. Good for physical activities, protection, and overcoming obstacles.',
        color: '#FF4444',
        keywords: ['Energy', 'Action', 'Courage', 'Conflict']
      },
      Mercury: {
        name: 'Mercury',
        icon: '☿',
        description: 'Communication, learning, travel, commerce. Good for studying, writing, and business matters.',
        color: '#87CEEB',
        keywords: ['Communication', 'Learning', 'Travel', 'Commerce']
      },
      Jupiter: {
        name: 'Jupiter',
        icon: '♃',
        description: 'Expansion, wisdom, luck, spirituality. Good for spiritual growth, learning, and attracting abundance.',
        color: '#FFB347',
        keywords: ['Expansion', 'Wisdom', 'Luck', 'Spirituality']
      },
      Venus: {
        name: 'Venus',
        icon: '♀',
        description: 'Love, beauty, harmony, relationships. Good for love spells, beauty rituals, and artistic endeavors.',
        color: '#FF69B4',
        keywords: ['Love', 'Beauty', 'Harmony', 'Relationships']
      },
      Saturn: {
        name: 'Saturn',
        icon: '♄',
        description: 'Discipline, structure, karma, limitations. Good for binding, protection, and serious matters.',
        color: '#708090',
        keywords: ['Discipline', 'Structure', 'Karma', 'Limitations']
      }
    };

    return planetData[planet] || planetData.Sun;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const planetaryHours = getPlanetaryHours(formData.date, formData.time);
      const currentPlanetInfo = getPlanetInfo(planetaryHours.currentPlanet);
      const dayRulerInfo = getPlanetInfo(planetaryHours.dayRuler);
      
      setPlanetaryHoursData({
        date: formData.date,
        time: formData.time,
        location: formData.location,
        ...planetaryHours,
        currentPlanetInfo,
        dayRulerInfo
      });
      
      setPage(1);
    } catch (error) {
      console.error('Error calculating planetary hours:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{
        backgroundImage: `url('/img/saturnwallpaper.jpg')`,
      }}
    >
      {page === 0 && (
        <div className="ascendant-display-container">
          <div className="ascendant-header">
            <div className="ascendant-title">
              <Clock size={24} />
              <h1>Planetary Hours Calculator</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ascendant-form">
            <div className="form-section">
              <div className="section-title">
                <Calendar size={18} />
                <span>Date & Time</span>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Time</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title">
                <MapPin size={18} />
                <span>Location (Optional)</span>
              </div>
              <div className="form-field">
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter location for additional context"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="calculate-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Clock size={18} />
                    Calculate Planetary Hours
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {page === 1 && planetaryHoursData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="ascendant-display-container"
        >
          <div className="ascendant-result">
            <div className="result-header">
              <h2>Planetary Hours</h2>
              <div className="ascendant-sign-display">
                <span className="sign-emoji" style={{ fontSize: '4rem' }}>{planetaryHoursData.currentPlanetInfo.icon}</span>
                <span className="sign-name">{planetaryHoursData.currentPlanetInfo.name} Hour</span>
              </div>
            </div>

            <div className="result-details">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                margin: '1.5rem 0 2.5rem 0',
                gap: '1.5rem',
              }}>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  color: '#fff',
                  fontWeight: 500,
                }}>
                  <span style={{ opacity: 0.7 }}>Date</span> <span style={{ fontWeight: 700 }}>{planetaryHoursData.date}</span>
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  color: '#fff',
                  fontWeight: 500,
                }}>
                  <span style={{ opacity: 0.7 }}>Day Ruler</span> <span style={{ fontWeight: 700 }}>{planetaryHoursData.dayRulerInfo.icon} {planetaryHoursData.dayRulerInfo.name}</span>
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  color: '#fff',
                  fontWeight: 500,
                }}>
                  <span style={{ opacity: 0.7 }}>Time</span> <span style={{ fontWeight: 700 }}>{planetaryHoursData.time}</span>
                </div>
              </div>
            </div>

            <div className="ascendant-description">
              <h3>Current Planetary Hour: {planetaryHoursData.currentPlanetInfo.name}</h3>
              <p>{planetaryHoursData.currentPlanetInfo.description}</p>
              
              <h4 style={{ marginTop: '1rem', color: '#FFC107' }}>Keywords:</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                {planetaryHoursData.currentPlanetInfo.keywords.map((keyword, index) => (
                  <span 
                    key={index}
                    style={{
                      background: 'rgba(255, 193, 7, 0.2)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      color: '#FFC107'
                    }}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>

        </motion.div>
      )}
    </div>
  );
};

export default PlanetaryHoursExperience; 
